"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { pushVirtualPageView, trackPageview } from "@/lib/analytics";

// Fires a first-party pageview on every client-side route change. Mounted once in
// the locale layout. Deduplicates the very first render so we don't double-count.
//
// The GTM leg is deliberately asymmetric: the initial load already produces a GA4
// page_view from the configuration tag, so only the client-side navigations after
// it need announcing, or the first page would be counted twice.
export function Analytics({ locale }: { locale: string }) {
  const pathname = usePathname();
  const last = useRef<string | null>(null);
  const initialLoad = useRef(true);

  useEffect(() => {
    if (last.current === pathname) return;
    last.current = pathname;
    trackPageview(pathname, locale);

    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    // Returned as the cleanup so a navigation that lands before the previous
    // push has fired supersedes it rather than racing it.
    return pushVirtualPageView();
  }, [pathname, locale]);

  return null;
}
